import { ExternalLink } from 'lucide-react';
import { Portfolio } from '../../../../types';
import CopyUrlButton from '../../../ui/copy-url-button';

interface PortfolioDetailProps {
  portfolio: Portfolio;
}

const PortfolioDetail = ({ portfolio }: PortfolioDetailProps) => {
  return (
    <div className="pt-2 pb-16 flex flex-col gap-6">
      {/* Title */}
      <div className="mb-4">
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2">
          {portfolio.title}
        </h2>
        {portfolio.subtitle && (
          <p className="text-base text-blackSecondary mb-3">
            {portfolio.subtitle}
          </p>
        )}
        {portfolio.categories.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {portfolio.categories
              .filter((category) => category !== 'All')
              .map((category, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
                >
                  {category}
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {portfolio.url && (
          <a
            href={portfolio.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm font-medium text-white bg-brand rounded-full px-4 py-2 hover:opacity-90 brand-transition"
          >
            <ExternalLink className="h-4 w-4" />
            View Project
          </a>
        )}
        <CopyUrlButton />
      </div>
      {/* Summary — projects without a long-form article show the case-study
          image, what the work was, and what was delivered. */}
      {portfolio.description && (
        <div className="flex flex-col gap-6">
          {/* Same 600px breakpoint the cards use, but handled by the browser so
              it follows a resize without re-rendering. */}
          <picture>
            <source
              media="(max-width: 599px)"
              srcSet={portfolio.mobileImageLink}
            />
            <img
              src={portfolio.image}
              alt={portfolio.alt}
              className="w-full object-cover rounded-lg"
            />
          </picture>
          <p className="text-base leading-relaxed">{portfolio.description}</p>
          {portfolio.skills && portfolio.skills.length > 0 && (
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-medium text-gray-900">
                Skills & deliverables
              </h3>
              <div className="flex flex-wrap gap-2">
                {portfolio.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Article */}
      <div className="flex flex-col gap-8" id="article">
        {portfolio.article}
      </div>
    </div>
  );
};

export default PortfolioDetail;
